import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useUser } from '@store';

interface UnassignDriverDialogProps {
  open: boolean;
  onClose: () => void;
  runId: number;
  /** Driver currently on the run — accepted OR just offered. */
  currentDriverName?: string | null;
  /** Called after a successful unassign so the page can refetch. */
  onDone?: () => void;
}

/**
 * Confirm-only dialog for removing a driver from a parts-delivery run without
 * immediately reassigning. POSTs to reassignPartsDeliveryRun with no
 * newDriverId — the server branches to the unassign path (deprecate bundles,
 * clear driver_id, set status 'draft'). Mirrors mobile service.
 */
const UnassignDriverDialog: React.FC<UnassignDriverDialogProps> = ({
  open,
  onClose,
  runId,
  currentDriverName,
  onDone,
}) => {
  const user = useUser();
  const [submitting, setSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const token = await user?.getToken();
      if (!token) {
        throw new Error('Authentication failed — could not get token');
      }
      const baseUrl = window.location.origin;
      const res = await axios({
        method: 'POST',
        url: `${baseUrl}/.netlify/functions/reassignPartsDeliveryRun`,
        data: { runId }, // no newDriverId → unassign mode
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Unassign returned non-success');
      }
      toast.success(
        currentDriverName ? `${currentDriverName} unassigned from this run` : 'Run unassigned',
      );
      onDone?.();
      onClose();
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error;
      toast.error(serverMsg || err?.message || 'Unassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Unassign driver</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            {currentDriverName ? (
              <>
                Remove <strong>{currentDriverName}</strong> from this run?
              </>
            ) : (
              <>Remove the current driver from this run?</>
            )}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            The driver will be notified the run was taken off their list. The run will
            move back to <strong>draft</strong> and won't be offered again until you
            redispatch it.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='warning'
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {submitting ? 'Unassigning…' : 'Unassign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnassignDriverDialog;
